  import { OnInit, Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PATH } from '@app/constants';
import { Developer } from '@app/interfaces';
import { DeveloperService } from '@app/services';
import { ModalService } from '@app/services/modal/modal.service';
import { faFileUpload, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FilePondOptions } from 'filepond';
import { forkJoin, tap } from 'rxjs';

@Component({
  selector: 'mnf-authentication-third-screen',
  templateUrl: './authentication-third-screen.component.html',
  styleUrls: ['./authentication-third-screen.component.scss']
})
export class AuthenticationThirdScreenComponent implements OnInit {

  /** Viewchilds de documentos a cargar por el solicitante */
  @ViewChild('pondine') pondine: any;
  @ViewChild('pondpowerIndividual') pondpowerIndividual: any;
  @ViewChild('pondCert') pondCert: any;
  @ViewChild('pondAct') pondAct: any;

  @Output() form = new EventEmitter();
  @Output() returnStep = new EventEmitter();
  @Input() set user(u: Developer) {
    this.developer = u;
  }

  /** @Gets */
  get forms() {
    return this.thirdFormDev.controls;
  }

  /** Valores Si o No */
  defaultOptions = ['Si', 'No'];

  /** Inicializar formulario */
  thirdFormDev: FormGroup;

  /** Variable global de developer */
  developer: Developer;

  /** Variables del modelo de developer */
  company: string;

  /** Inicializar variables globales para documentos a subir */
  certDefined: string;
  certFile: string;
  certTmpFile: File;
  certName = '';

  actDefined: string;
  actFile: string;
  actTmpFile: File;
  actName = '';

  INEDefined: string;
  INEFile: string;
  INETmpFile: File;
  INEName = '';

  certFileDefined: any;
  certFileFile: any;

  powerIndividualDefined: string;
  powerIndividualFile: string;
  powerIndividualTmpFile: File;
  powerIndividualName = '';

  /** Formato de archivo a aceptar */
  acceptableFiles = [
    'image/png',
    'image/jpeg',
    'application/pdf',
    'application/msword',
    'application/pdf',
    'application/xml',
    'image/webp',
    'application/zip',
    'application/x-zip-compressed'
  ];

  certOptions: FilePondOptions = {
    labelIdle: '* Cédula de identificación fiscal de la empresa',
    labelFileTypeNotAllowed: 'Tipo de archivo no válido',
    labelFileSizeNotAvailable: 'Tamaño máximo de 15 mb',
    chunkSize: 15728640,
    acceptedFileTypes: this.acceptableFiles
  }

  actOptions: FilePondOptions = {
    labelIdle: '* Acta constitutiva',
    labelFileTypeNotAllowed: 'Tipo de archivo no válido',
    labelFileSizeNotAvailable: 'Tamaño máximo de 15 mb',
    chunkSize: 15728640,
    acceptedFileTypes: this.acceptableFiles
  }

  ineOptions: FilePondOptions = {
    labelIdle: '* Credencial de elector - Ambos lados (INE/IFE) del representante legal',
    labelFileTypeNotAllowed: 'Tipo de archivo no válido',
    labelFileSizeNotAvailable: 'Tamaño máximo de 15 mb',
    chunkSize: 15728640,
    acceptedFileTypes: this.acceptableFiles
  }

  powerIndividualOptions: FilePondOptions = {
    labelIdle: '* Poder individual',
    labelFileTypeNotAllowed: 'Tipo de archivo no válido',
    labelFileSizeNotAvailable: 'Tamaño máximo de 15 mb',
    chunkSize: 15728640,
    acceptedFileTypes: this.acceptableFiles
  }

  certFiles: FilePondOptions['files'] = [];
  actFiles: FilePondOptions['files'] = [];
  ineFiles: FilePondOptions['files'] = [];
  powerIndividualFiles: FilePondOptions['files'] = [];

  iconReUpload = faFileUpload;
  iconX = faXmark;

  /** Email */
  email: string;

  constructor(private developServ: DeveloperService, private modalServ: ModalService, private router: Router) {}

  ngOnInit(): void {

    this.email = this.developer.email;

    this.company = this.developer?.developer?.company;
    this.INEFile = this.developer?.developer?.info?.ine;
    this.actFile = this.developer?.developer?.info?.act;
    this.certFile = this.developer?.developer?.info?.cert;
    this.powerIndividualFile = this.developer?.developer?.info?.powerIndividual;

    /** Inicializar formulario */
    this.thirdFormDev = new FormGroup({
      powerInConstitutiveAct: new FormControl(this.developer?.developer?.info?.powerInConstitutiveAct || '', [Validators.compose([Validators.required])]),
      pageToFoundPowerInAct: new FormControl(this.developer?.developer?.info?.pageToFoundPowerInAct || '', [Validators.compose([Validators.minLength(1), Validators.maxLength(8)])])
    })

  }

  /** Función para subir archivos al S3 */
  pondHandleAddFile(event: any, type: 'cert' | 'act' | 'ine' | 'powerIndividual') {
    if(!event.error) {
      if(type === 'cert') {
        const file = this.pondCert.getFile().file;
        this.certTmpFile = file;
      }
      if(type === 'act') {
        const file = this.pondAct.getFile().file;
        this.actTmpFile = file;
      }
      if(type === 'ine') {
        const file = this.pondine.getFile().file;
        this.INETmpFile = file;
      }
      if(type === 'powerIndividual') {
        const file = this.pondpowerIndividual.getFile().file;
        this.powerIndividualTmpFile = file;
      }
    }
  }

  /** Función para eliminar archivos subidos */
  deleteFile(type: 'cert' | 'act' | 'ine' | 'powerIndividual') {
    if(type === 'cert') {
      this.certTmpFile = null;
    }
    if(type === 'act') {
      this.actTmpFile = null;
    }
    if(type === 'ine') {
      this.INETmpFile = null;
    }
    if(type === 'powerIndividual') {
      this.powerIndividualTmpFile = null;
    }
  }

  finishRegister() {

    const requests: { ine?: any, cert?: any, act?: any, power?: any, info?: any } = {};

    const infoToSend = this.thirdFormDev.value;

    if(!this.INEFile && this.INETmpFile) {
      requests.ine = this.developServ.uploadDocuments(this.INETmpFile, 'ine').pipe(tap(res => this.INEFile = this.INETmpFile.name))
    }

    if(!this.certFile && this.certTmpFile) {
      requests.cert = this.developServ.uploadDocuments(this.certTmpFile, 'cert').pipe(tap(res => this.certFile = this.certTmpFile.name))
    }

    if(!this.actFile && this.actTmpFile) {
      requests.act = this.developServ.uploadDocuments(this.actTmpFile, 'act').pipe(tap(res => this.actFile = this.actTmpFile.name))
    }

    if(!this.powerIndividualFile && this.powerIndividualTmpFile) {
      requests.power = this.developServ.uploadDocuments(this.powerIndividualTmpFile, 'powerIndividual').pipe(tap(res => this.powerIndividualFile = this.powerIndividualTmpFile.name))
    }

    requests.info = this.developServ.updateInfoDevStepThree(infoToSend, this.email);

    forkJoin(requests).subscribe((r: { ine?: any, cert?: any, act?: any, power?: any, info?: any }) => {
      const modal = this.modalServ.modalCustom({
        header: 'Registro terminado',
        img: 'https://static.monific.com/assets/assets-app/img/icon/img/reg-success.svg',
        title: '¡Registro exitoso!',
        message: 'Gracias por elegir Monific, recibimos tu solicitud, nuestro equipo de análisis te contactara en un periodo de 24hrs a 48hrs, estamos validando tu información.',
        buttonText: 'Continuar'
      });

      modal.beforeClosed().subscribe(() => {
        r.info.regStep === 3
        this.form.next(r.info.regStep);
      });
    })

  }

  validForm() {
    if(this.thirdFormDev.valid && (this.INEFile || this.certFile || this.actFile)) {
      this.validFormDocsUp();
    } else if(!(this.thirdFormDev.valid && (this.INETmpFile || this.certTmpFile || this.actTmpFile))) {
      this.validFormDocsNoUp();
    }

  }

  validFormDocsUp() {
    return (this.thirdFormDev.valid && (this.INEFile || this.certFile || this.actFile));
  }

  validFormDocsNoUp() {
    return !(this.thirdFormDev.valid && (this.INETmpFile || this.certTmpFile || this.actTmpFile));
  }

  returnToStep() {
    this.returnStep.next(3);
  }

}
